// angular import
import { Component, Input } from '@angular/core';
import { NavigationItem } from '../../../layout/admin/navigation/navigation';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CpBpBreadcrumbContextService } from 'src/app/admin/cp-admin/cp-bp-management/cp-bp-management/cp-customer/service/cp-bp-breadcrumb-context.service';
import { TfService } from 'src/app/tf/tf-service.service';
import { LanguageService } from 'src/app/multi-language/language.service';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
import {
  parseCategoryPageNameForDevice,
  resolveCategoryLayoutDeviceFromViewport,
} from 'src/app/admin/ad-admin/ad-layout/category-layout.util';
import { CfCategoryService } from 'src/app/customer/cf-customer/cf-category/service/cf-category.service';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  host: {
    /* Category mobile shell only — product detail keeps original breadcrumb visibility */
    '[class.breadcrumb--show-mobile]': 'shouldShowOnMobile()',
    '[class.breadcrumb--detail-product]': 'isOnDetailProduct()',
  },
})
export class BreadcrumbComponent {
  // public props
  @Input() type: string;
  navigation: any;
  breadcrumbList: Array<any> = [];
  navigationList: any;
  isDetailProductRoute = false;
  isCategoryBrowseRoute = false;
  isSubCategoryRoute = false;

  private static readonly CATEGORY_NAV_TITLE = 'CFDashboard.Categories';
  private readonly defaultProductBackUrl = '/customer/CFProduct/product-view';

  // constructor
  constructor(
    private _router: Router,
    private location: Location,
    public nav: NavigationItem,
    private titleService: Title,
    public translate: TranslateService,
    private cpBpBreadcrumbContext: CpBpBreadcrumbContextService,
    private tfService: TfService,
    private languageService: LanguageService,
    private portalLayoutResolve: PortalLayoutResolveService,
    private categoryService: CfCategoryService,
  ) {
    this.navigation = this.nav.get();
    this.syncRouteFlags(this._router.url.split('?')[0]);
    this.setBreadcrumb();
    this.type = 'theme2';
  }

  // public method
  setBreadcrumb() {
    merge(
      this._router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
      this.cpBpBreadcrumbContext.refresh$.pipe(map(() => null)),
      this.portalLayoutResolve.watchComponent('category').pipe(map(() => null)),
      this.languageService.currentLanguage$.pipe(map(() => null)),
      this.categoryService.navigationHistory$.pipe(map(() => null)),
    ).subscribe((e) => {
      const activeLink =
        e instanceof NavigationEnd
          ? e.urlAfterRedirects.split('?')[0]
          : this._router.url.split('?')[0];
      if (activeLink) {
        this.syncRouteFlags(activeLink);
        this.breadcrumbList.length = 0;
        this.filterNavigation(activeLink);
      }
    });
  }

  private syncRouteFlags(path: string): void {
    const clean = (path || '').split('?')[0];
    this.isDetailProductRoute = clean.includes('/detail-product/');
    this.isSubCategoryRoute = clean.startsWith('/sub-category/');
    this.isCategoryBrowseRoute = clean === '/category' || this.isSubCategoryRoute;
  }

  private isAdminCustomerCategoryManagementRoute(pathOnly: string): boolean {
    return (
      this.tfService.isAdminCustomer() &&
      pathOnly.includes('/admin/cf-product/navigation-product')
    );
  }

  private cpBpPortalIdAfter(activeLink: string, marker: string): string | null {
    const idx = activeLink.indexOf(marker);
    if (idx === -1) {
      return null;
    }
    const tail = activeLink.slice(idx + marker.length).split('?')[0];
    const seg = tail.split('/').filter(Boolean)[0];
    return seg != null && seg !== '' ? seg : null;
  }

  filterNavigation(activeLink) {
    this.cpBpBreadcrumbContext.invalidateRelationshipTrailIfNeeded(activeLink);

    const product = JSON.parse(localStorage.getItem('ProductByCategory'));
    let result: any = null;
    let title = 'Welcome';
    // Extract query params if any
    const queryString = activeLink.split('?')[1] || '';
    const queryParams = new URLSearchParams(queryString);
    const routerQueryParams = new URLSearchParams(this._router.url.split('?')[1] || '');
    const settingsTabParam = queryParams.get('settings_tab') ?? routerQueryParams.get('settings_tab');
    const permissionTabParam = queryParams.get('permission_tab') ?? routerQueryParams.get('permission_tab');
    const productTabParam = queryParams.get('tab') ?? routerQueryParams.get('tab');
    const pathOnly = (activeLink || '').split('?')[0];

    // Customer categories — Home > Products > Categories [> nested]
    if (pathOnly === '/category' || pathOnly.startsWith('/sub-category/')) {
      this.navigationList = this.buildCustomerCategoryBreadcrumbs(pathOnly);
      const last = this.navigationList[this.navigationList.length - 1];
      const lastTitle =
        last?.translateTitle === false
          ? last.title
          : this.translate.instant(last?.title || BreadcrumbComponent.CATEGORY_NAV_TITLE);
      this.titleService.setTitle(lastTitle + ' | ZUMConnect');
      return;
    }
    
    // Check if it's a detail-product route
    const isDetailProduct = activeLink.includes('/detail-product/');
    
    // Check if it's a product details route (admin side)
    const isProductDetails = activeLink.includes('/admin/cf-product/product-details/');
    
    // Check if it's a product tab route
    const isProductTab = activeLink === '/customer/CFProduct/all' ||
                        activeLink === '/customer/CFProduct/product-news' ||
                        activeLink === '/category' ||
                        activeLink === '/customer/favorite-products' ||
                        activeLink === '/compare'
    
    // Check if it's a settings navigation route
    const isSettingsRoute = activeLink.includes('/admin/configuration/settings-navigation');
    
    // Check if it's a product navigation route
    const isProductNavigationRoute = activeLink.includes('/navigation-product') || activeLink.includes('/admin/cf-product/navigation-product');

    // Check if it's a permission nested route
    const isPermissionRoute = activeLink.includes('/admin/configuration/permission-navigation/');

    // Check if it's a Business Partner Management route
    const isBpManagementRoute = activeLink.includes('/admin/cp-bp-management/');

    // Images, Videos, and Documents share the media library breadcrumb.
    const mediaPath = pathOnly.replace(/\/$/, '');
    const isMediaImages = mediaPath === '/admin/cf-images';
    const isMediaVideos = mediaPath === '/admin/cf-videos';
    const isMediaDocuments = mediaPath === '/admin/cf-documents';

    // Handle permission nested routes
    if (isMediaImages || isMediaVideos || isMediaDocuments) {
      const sectionTitle = isMediaVideos
        ? 'CfVideos.library.videos'
        : isMediaDocuments
          ? 'CfDocuments.library.documents'
          : 'CfImages.library.images';
      result = [
        {
          url: '/admin/cf-images',
          title: 'CfImages.title',
          breadcrumbs: true,
          type: 'item'
        },
        {
          url: false,
          title: sectionTitle,
          breadcrumbs: true,
          type: 'item'
        }
      ];
      title = isMediaVideos
        ? 'CfVideos.title'
        : isMediaDocuments
          ? 'CfDocuments.title'
          : 'CfImages.title';
    }

    else if (isPermissionRoute) {
      result = [
        {
          url: '/admin/configuration/settings-navigation?settings_tab=permission',
          title: 'Settings',
          breadcrumbs: true,
          type: 'item'
        },
        {
          url: '/admin/configuration/settings-navigation?settings_tab=permission',
          title: 'CFPermissions.permission',
          breadcrumbs: true,
          type: 'item'
        }
      ];

      // Extract nested segments after permission-navigation/
      const segments = activeLink.split('/admin/configuration/permission-navigation/')[1]?.split('?')[0]?.split('/') || [];

      // Generate breadcrumb objects for each segment
      let currentPath = '/admin/configuration/permission-navigation';
      segments.forEach(segment => {
        if (segment) {
          currentPath += '/' + segment;
          result.push({
            url: currentPath,
            title: segment.charAt(0).toUpperCase() + segment.slice(1), // capitalize
            breadcrumbs: true,
            type: 'item'
          });
        }
      });
    }

    // Handle settings route specially
    else if (isSettingsRoute) {
      result = [
        {
          url: '/admin/configuration/settings-navigation',
          title: 'Settings',
          breadcrumbs: true,
          type: 'item'
        }
      ];
      // Default to first settings tab when none provided
      if (!settingsTabParam) {
        result.push({
          url: '/admin/configuration/settings-navigation?settings_tab=config-ln',
          title: 'TFConfigurations.configrationLN',
          breadcrumbs: true,
          type: 'item'
        });
        title = 'TFConfigurations.configrationLN';
      } else {
        title = 'Settings';
      }
    }
    
    // Handle product navigation route specially
    else if (isProductNavigationRoute) {
      if (this.isAdminCustomerCategoryManagementRoute(pathOnly)) {
        result = [
          {
            url: false,
            title: 'CATEGORY_MANAGEMENT',
            breadcrumbs: true,
            type: 'item',
          },
        ];
        title = 'CATEGORY_MANAGEMENT';
      } else {
        const isAdminProductNav = pathOnly.includes('/admin/cf-product/navigation-product');
        const productNavBase = isAdminProductNav
          ? '/admin/cf-product/navigation-product'
          : '/customer/CFProduct/navigation-product';
        const effectiveProductTab =
          productTabParam ?? (this.tfService.isAdminCustomer() ? 'categorie' : null);

        result = [
          {
            url: productNavBase,
            title: 'CF_Product.ProductManagement',
            breadcrumbs: true,
            type: 'item',
          },
        ];
        // Default to first product tab when none provided
        if (!effectiveProductTab) {
          result.push({
            url: `${productNavBase}?tab=item-list`,
            title: 'CF_Product.ItemList',
            breadcrumbs: true,
            type: 'item',
          });
          title = 'CF_Product.ItemList';
        } else {
          title = 'CF_Product.ProductManagement';
        }
      }
    }

    // Handle Business Partner Management routes
    else if (isBpManagementRoute) {
      const cpBpListUrl = '/admin/cp-bp-management/cp-customer/customer-list';
      const allBpsKey = 'cpCustomerList.allBps';

      result = [
        {
          url: cpBpListUrl,
          title: 'BUSINESS_PARTNER',
          breadcrumbs: true,
          type: 'item'
        }
      ];

      if (activeLink.includes('/contact-list')) {
        const contactBpId = this.cpBpPortalIdAfter(activeLink, '/contact-list/');
        if (contactBpId) {
          const hasName =
            this.cpBpBreadcrumbContext.currentPortalId === contactBpId &&
            this.cpBpBreadcrumbContext.currentBpName;
          const childLabel = hasName
            ? this.cpBpBreadcrumbContext.currentBpName!
            : contactBpId;
          const relTrail = this.cpBpBreadcrumbContext.getRelationshipDetailTrail();
          const useParentChildTrail =
            !!relTrail &&
            relTrail.childPortalId === contactBpId &&
            relTrail.parentPortalId !== contactBpId;

          if (useParentChildTrail && relTrail) {
            result = [
              {
                url: `/admin/cp-bp-management/cp-customer/relationship-list/${relTrail.parentPortalId}`,
                title: relTrail.parentName,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: `/admin/cp-bp-management/cp-customer/customer-details/${contactBpId}`,
                title: childLabel,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: false,
                title: 'CONTACT_LIST',
                breadcrumbs: true,
                type: 'item'
              }
            ];
          } else {
            result = [
              {
                url: `/admin/cp-bp-management/cp-customer/customer-details/${contactBpId}`,
                title: childLabel,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: false,
                title: 'CONTACT_LIST',
                breadcrumbs: true,
                type: 'item'
              }
            ];
          }
          title = 'CONTACT_LIST';
        } else {
          result.push({
            url: '/admin/cp-bp-management/contact-list',
            title: 'CONTACT_LIST',
            breadcrumbs: true,
            type: 'item'
          });
          title = 'CONTACT_LIST';
        }
      } else if (activeLink.includes('/contact-create')) {
        const portalContactId = this.cpBpPortalIdAfter(activeLink, '/contact-create/');
        const contactFormTitle = portalContactId
          ? 'CPContactCreateUpdate.updateContact'
          : 'CREATE_CONTACT';
        result.push({
          url: false,
          title: contactFormTitle,
          breadcrumbs: true,
          type: 'item'
        });
        title = contactFormTitle;
      } else if (activeLink.includes('/cp-customer/customer-list')) {
        result.push({
          url: false,
          title: allBpsKey,
          breadcrumbs: true,
          type: 'item'
        });
        title = allBpsKey;
      } else if (activeLink.includes('/cp-customer/customer-details/')) {
        const id = this.cpBpPortalIdAfter(activeLink, '/cp-customer/customer-details/');
        const relTrail = this.cpBpBreadcrumbContext.getRelationshipDetailTrail();
        const useRelTrail =
          !!id &&
          !!relTrail &&
          relTrail.childPortalId === id &&
          relTrail.parentPortalId !== id;

        if (useRelTrail && relTrail && id) {
          result.push({
            url: `/admin/cp-bp-management/cp-customer/relationship-list/${relTrail.parentPortalId}`,
            title: relTrail.parentName,
            translateTitle: false,
            breadcrumbs: true,
            type: 'item'
          });
          const childLabel =
            this.cpBpBreadcrumbContext.currentPortalId === id && this.cpBpBreadcrumbContext.currentBpName
              ? this.cpBpBreadcrumbContext.currentBpName
              : id;
          result.push({
            url: false,
            title: childLabel,
            translateTitle: false,
            breadcrumbs: true,
            type: 'item'
          });
          title = childLabel;
        } else if (id) {
          result.push({
            url: cpBpListUrl,
            title: allBpsKey,
            breadcrumbs: true,
            type: 'item'
          });
          const hasName =
            this.cpBpBreadcrumbContext.currentPortalId === id && this.cpBpBreadcrumbContext.currentBpName;
          const nameLabel = hasName ? this.cpBpBreadcrumbContext.currentBpName! : 'BUSINESS_PARTNER';
          result.push({
            url: false,
            title: nameLabel,
            translateTitle: !hasName,
            breadcrumbs: true,
            type: 'item'
          });
          title = nameLabel;
        }
      } else if (activeLink.includes('/cp-customer/relationship-list/')) {
        const id = this.cpBpPortalIdAfter(activeLink, '/cp-customer/relationship-list/');
        if (id) {
          const hasName =
            this.cpBpBreadcrumbContext.currentPortalId === id &&
            this.cpBpBreadcrumbContext.currentBpName;
          const childLabel = hasName ? this.cpBpBreadcrumbContext.currentBpName! : id;
          const relTrail = this.cpBpBreadcrumbContext.getRelationshipDetailTrail();
          const useParentChildTrail =
            !!relTrail &&
            relTrail.childPortalId === id &&
            relTrail.parentPortalId !== id;

          if (useParentChildTrail && relTrail) {
            result = [
              {
                url: `/admin/cp-bp-management/cp-customer/relationship-list/${relTrail.parentPortalId}`,
                title: relTrail.parentName,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: `/admin/cp-bp-management/cp-customer/customer-details/${id}`,
                title: childLabel,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: false,
                title: 'cpCustomerList.relationship',
                breadcrumbs: true,
                type: 'item'
              }
            ];
          } else {
            result = [
              {
                url: `/admin/cp-bp-management/cp-customer/customer-details/${id}`,
                title: childLabel,
                translateTitle: false,
                breadcrumbs: true,
                type: 'item'
              },
              {
                url: false,
                title: 'cpCustomerList.relationship',
                breadcrumbs: true,
                type: 'item'
              }
            ];
          }
          title = 'cpCustomerList.relationship';
        }
      } else if (activeLink.includes('/cp-customer/customer-create')) {
        result.push({
          url: cpBpListUrl,
          title: allBpsKey,
          breadcrumbs: true,
          type: 'item'
        });
        const editId = this.cpBpPortalIdAfter(activeLink, '/cp-customer/customer-create/');
        if (editId) {
          const hasName =
            this.cpBpBreadcrumbContext.currentPortalId === editId &&
            this.cpBpBreadcrumbContext.currentBpName;
          result.push({
            url: false,
            title: hasName
              ? this.cpBpBreadcrumbContext.currentBpName!
              : 'CPCustomerCreateUpdate.titleUpdateCustomer',
            translateTitle: !hasName,
            breadcrumbs: true,
            type: 'item'
          });
          title = hasName
            ? this.cpBpBreadcrumbContext.currentBpName!
            : 'CPCustomerCreateUpdate.titleUpdateCustomer';
        } else {
          result.push({
            url: false,
            title: 'cpCustomerList.createBp',
            breadcrumbs: true,
            type: 'item'
          });
          title = 'cpCustomerList.createBp';
        }
      } else if (activeLink.includes('/cf-categorie')) {
        result.push({
          url: false,
          title: 'CATEGORY_BP_MANAGEMENT',
          breadcrumbs: true,
          type: 'item'
        });
        title = 'CATEGORY_BP_MANAGEMENT';
      } else if (activeLink.includes('/permission')) {
        result.push({
          url: '/admin/cp-bp-management/permission',
          title: 'CFPermissions.permission',
          breadcrumbs: true,
          type: 'item'
        });
        title = 'CFPermissions.permission';
      } else if (activeLink.includes('/cp-customer')) {
        result.push({
          url: false,
          title: allBpsKey,
          breadcrumbs: true,
          type: 'item'
        });
        title = allBpsKey;
      } else {
        result.push({
          url: '/admin/cp-bp-management/contact-list',
          title: 'CONTACT_LIST',
          breadcrumbs: true,
          type: 'item'
        });
        title = 'CONTACT_LIST';
      }
    }
    
    // Handle product details route (admin side)
    else if (isProductDetails) {
      // Extract product name from URL
      const productName = decodeURIComponent(activeLink.split('/admin/cf-product/product-details/')[1]?.split('?')[0] || '');
      
      result = [
        {
          url: '/admin/cf-product/navigation-product',
          title: 'CF_Product.ProductManagement',
          breadcrumbs: true,
          type: 'item'
        },
        {
          url: false,
          title: productName || 'Product Details',
          breadcrumbs: true,
          type: 'item'
        }
      ];
      title = productName || 'Product Details';
    }
    
    else {
      this.navigation.forEach((a) => {
        if (a.type === 'item' && 'url' in a && a.url === activeLink) {
          result = [
            {
              url: 'url' in a ? a.url : false,
              title: a.title,
              breadcrumbs: 'breadcrumbs' in a ? a.breadcrumbs : true,
              type: a.type
            }
          ];
          title = a.title;
        } else if (a.type === 'group' && 'children' in a) {
          a.children.forEach((b) => {
            // Handle detail-product route with dynamic ID
            if (b.type === 'item' && 'url' in b && isDetailProduct && b.url === '/customer/CFDashboard/detail-product') {
              result = [
                {
                  url: '/customer/CFDashboard/dashboard',
                  title: 'CfHome.Home',
                  breadcrumbs: true,
                  type: 'item'
                },
                {
                  url: '/customer/CFProduct/product-view',
                  title: 'PRODUCT',
                  breadcrumbs: true,
                  type: 'item'
                },
                {
                  url: false,
                  title: 'PRODUCT_DETAILS',
                  breadcrumbs: true,
                  type: b.type
                }
              ];
              title = 'PRODUCT_DETAILS';
            }
            // Handle product tab routes (all, news, category)
            else if (b.type === 'item' && 'url' in b && isProductTab && b.url === activeLink) {
              const productId = product?.id || ''; // get from localStorage or route
              result = [
                {
                  url: '/customer/CFProduct/product-view', // parent PRODUCT breadcrumb
                  title: 'PRODUCT',
                  breadcrumbs: true,
                  type: 'item'
                },
                {
                  url: `/customer/CFDashboard/detail-product/${productId}`, // clickable product info
                  title: b.title,
                  breadcrumbs: true,
                  type: b.type
                }
              ];
            }

            // Exact match for other routes
            else if (b.type === 'item' && 'url' in b && b.url === activeLink) {
              result = [
                {
                  url: 'url' in b ? b.url : false,
                  title: b.title,
                  breadcrumbs: 'breadcrumbs' in b ? b.breadcrumbs : true,
                  type: b.type
                }
              ];
              title = b.title;
            } 
            else if (b.type === 'collapse' && 'children' in b) {
              b.children.forEach((c) => {
                if (c.type === 'item' && 'url' in c && c.url === activeLink) {
                  result = [
                    {
                      url: 'url' in b ? b.url : false,
                      title: b.title,
                      breadcrumbs: 'breadcrumbs' in b ? b.breadcrumbs : true,
                      type: b.type
                    },
                    {
                      url: 'url' in c ? c.url : false,
                      title: c.title,
                      breadcrumbs: 'breadcrumbs' in c ? c.breadcrumbs : true,
                      type: c.type
                    }
                  ];
                  title = c.title;
                }
              });
            }
          });
        }
      });
    }
    
    this.navigationList = result;
    this.applyCategoryPageNameToBreadcrumbs(activeLink);

    if (product && product.path && product.path.length !== 0 && this.navigationList && this.navigationList.some(item => item.title === 'Structure')) {
      const menuObjects = product.path.map((menuString) => ({
        breadcrumbs: true,
        title: menuString,
        type: 'item',
        url: '/customer/CFCategory/CfTreeView',
      }));

      this.navigationList = [...this.navigationList, ...menuObjects];
    }

    const path = (activeLink || '').split('?')[0];
    if (path === '/category' || path.startsWith('/sub-category/')) {
      const categoryLabel = this.resolveCategoryBreadcrumbLabel();
      title = categoryLabel.translateTitle
        ? this.translate.instant(categoryLabel.title)
        : categoryLabel.title;
      this.titleService.setTitle(title + ' | ZUMConnect');
      return;
    }
    
    const translatedTitle = this.translate.instant(title);
    this.titleService.setTitle(translatedTitle + ' | ZUMConnect');
  }

  
  isOnDashboard(): boolean {
    const path = this._router.url.split('?')[0];
    return (
      path === '/customer/CFDashboard/dashboard' ||
      path === '/customer/CFDashboard/admin-customer-dashboard'
    );
  }

  isOnDetailProduct(): boolean {
    return this.isDetailProductRoute;
  }

  /** Mobile: show breadcrumb on subcategory detail only — not on category list (tabs replace it). */
  shouldShowOnMobile(): boolean {
    return this.isSubCategoryRoute;
  }

  /** Category subcategory pages: breadcrumb + back (Figma). */
  showCategoryMobileBack(): boolean {
    return this.isSubCategoryRoute;
  }

  /** Product detail keeps its original back; subcategory uses category back. */
  showToolbarBack(): boolean {
    return this.isOnDetailProduct() || this.showCategoryMobileBack();
  }

  goBackFromDetailProduct(): void {
    const returnUrl = this._router.parseUrl(this._router.url).queryParams['returnUrl'];
    if (typeof returnUrl === 'string' && this.isSafeCustomerReturnUrl(returnUrl)) {
      this._router.navigateByUrl(returnUrl);
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.location.back();
      return;
    }
    this._router.navigateByUrl(this.defaultProductBackUrl);
  }

  goBackFromToolbar(): void {
    if (this.isDetailProductRoute) {
      this.goBackFromDetailProduct();
      return;
    }
    if (this.isSubCategoryRoute) {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.location.back();
        return;
      }
      this._router.navigate(['/category']);
    }
  }

  private isSafeCustomerReturnUrl(url: string): boolean {
    const trimmed = url.trim();
    if (!trimmed.startsWith('/customer/') || trimmed.includes('//')) {
      return false;
    }
    if (/^[a-z]+:/i.test(trimmed)) {
      return false;
    }
    return true;
  }
  
  isOnProductTab(): boolean {
    const productUrls = [
      '/customer/CFProduct/all',
      '/customer/CFProduct/product-news',
      '/category'
    ];
    return productUrls.some(url => this._router.url.includes(url));
  }
  
  isProductParent(breadcrumb: any): boolean {
    // Don't show "PRODUCT" in the breadcrumb list if we're on a product tab
    // because we're manually adding it above
    return breadcrumb.title === 'PRODUCT' && this.isOnProductTab();
  }

  /**
   * Prefer layout `payload.pageName` for category breadcrumbs (same source as product nav tabs).
   * Covers `/category` and `/sub-category/...`.
   */
  private applyCategoryPageNameToBreadcrumbs(activeLink: string): void {
    const path = (activeLink || '').split('?')[0];
    const onCategoryRoute = path === '/category' || path.startsWith('/sub-category/');
    if (!onCategoryRoute) {
      return;
    }

    const label = this.resolveCategoryBreadcrumbLabel();

    if (!this.navigationList?.length) {
      this.navigationList = [
        {
          url: '/category',
          title: label.title,
          translateTitle: label.translateTitle,
          breadcrumbs: true,
          type: 'item',
        },
      ];
      return;
    }

    this.navigationList = this.navigationList.map((item: any) => {
      if (item?.title !== BreadcrumbComponent.CATEGORY_NAV_TITLE) {
        return item;
      }
      return {
        ...item,
        title: label.title,
        translateTitle: label.translateTitle,
      };
    });
  }

  /** Categories [> subcategory names] — no Home/Products prefix. */
  private buildCustomerCategoryBreadcrumbs(path: string): any[] {
    const categoryLabel = this.resolveCategoryBreadcrumbLabel();
    const crumbs: any[] = [
      {
        url: path.startsWith('/sub-category/') ? '/category' : false,
        title: categoryLabel.title,
        translateTitle: categoryLabel.translateTitle,
        breadcrumbs: true,
        type: 'item',
      },
    ];

    if (!path.startsWith('/sub-category/')) {
      return crumbs;
    }

    const codeFromUrl = decodeURIComponent(path.replace(/^\/sub-category\//, '').split('/')[0] || '');
    const history = this.categoryService.getNavigationHistory();
    const codes =
      history.length > 0
        ? history
        : codeFromUrl
          ? [codeFromUrl]
          : [];

    codes.forEach((code, index) => {
      const isLast = index === codes.length - 1;
      crumbs.push({
        url: isLast ? false : `/sub-category/${code}`,
        title: this.categoryService.getCategoryName(code) || code,
        translateTitle: false,
        breadcrumbs: true,
        type: 'item',
      });
    });

    return crumbs;
  }

  private resolveCategoryBreadcrumbLabel(): { title: string; translateTitle: boolean } {
    const custom = parseCategoryPageNameForDevice(
      this.portalLayoutResolve.getEntry('category')?.payload ?? null,
      this.languageService.getCurrentLanguage(),
      resolveCategoryLayoutDeviceFromViewport(),
    );
    if (custom) {
      return { title: custom, translateTitle: false };
    }
    return {
      title: BreadcrumbComponent.CATEGORY_NAV_TITLE,
      translateTitle: true,
    };
  }
  
  getCurrentTabTitle(): string {
    const url = this._router.url;
    if (url.includes('/customer/CFProduct/all')) {
      return 'CFDashboard.ALL';
    } else if (url.includes('/customer/CFProduct/product-news')) {
      return 'CFDashboard.News';
    } else if (url.includes('/category') || url.includes('/sub-category/')) {
      return this.resolveCategoryBreadcrumbLabel().title;
    }
    return '';
  }
  
  /** Count of breadcrumb items rendered in the template (used for mobile single-crumb hide). */
  get visibleBreadcrumbCount(): number {
    const listCount = this.navigationList?.length ?? 0;
    const activeTabExtra = this.getActiveTabTitle() ? 1 : 0;
    return listCount + activeTabExtra;
  }

  /** On 320px–430px viewports, hide the bar when there is only one crumb (e.g. top-level "Products"). */
  get hideSingleCrumbOnMobile(): boolean {
    return this.visibleBreadcrumbCount <= 1;
  }

  getActiveTabTitle(): string | null {
    const url = this._router.url;
    
    // Extract query params from URL
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const settingsTab = urlParams.get('settings_tab');
    const permissionTab = urlParams.get('permission_tab');
    const productTab = urlParams.get('tab');
    
    // Check if on settings page (but not on permission nested routes)
    if (url.includes('/settings-navigation') && settingsTab && !url.includes('/admin/permission/')) {
      const tabTitles: { [key: string]: string } = {
        'config-ln': 'TFConfigurations.configrationLN',
        'company': 'TFConfigurations.company',
        'language': 'TFConfigurations.language',
        'admin-controls': 'TFConfigurations.adminControls',
        'tax': 'TFConfigurations.tax',
        'email-template': 'TFConfigurations.emailTemplate',
        'permission': 'CFPermissions.permission',
        'flexible-template': 'TFConfigurations.flexibleTemplate',
        'invitation': 'TFConfigurations.invitation',
        'environment': 'TFConfigurations.environment',
        'security': 'TFConfigurations.security'
      };
      return tabTitles[settingsTab] || null;
    }
    
    // Check if on permission sub-tab (only when NOT on nested permission routes)
    if (settingsTab === 'permission' && permissionTab && !url.includes('/admin/permission/')) {
      const permissionTabTitles: { [key: string]: string } = {
        'user-permission': 'User Permission',
        'role-permission': 'Role Permission',
        // Add other permission tab titles as needed
      };
      return permissionTabTitles[permissionTab] || null;
    }
    
    // Check if on product navigation page
    if (url.includes('/navigation-product') && productTab) {
      const pathOnly = url.split('?')[0];
      if (this.isAdminCustomerCategoryManagementRoute(pathOnly)) {
        return null;
      }
      const productTabTitles: { [key: string]: string } = {
        'item-list': 'CF_Product.ItemList',
        'specification': 'CF_Product.Specifications',
        'categorie': 'CF_Product.Categorie'
      };
      return productTabTitles[productTab] || null;
    }
    
    return null;
  }
}